import * as React from 'react';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Box from '@mui/material/Box';
import PublicIcon from '@mui/icons-material/Public';
import { countriesAtlas } from '../countriesApi';
import { useLocalization } from '@fluent/react';

export type CountryOption = {
  code: string;
  name: string;
};

export interface CountryMultiSelectProps {
  value?: string[] | null;
  onChange: (names: string[] | null, options: CountryOption[] | null) => void;

  label?: string;
  placeholder?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  sx?: any;
}

const toOptions = () =>
  countriesAtlas
    .map(c => ({ code: c.code, name: c.label }))
    .sort((a, b) => a.name.localeCompare(b.name));

const filterOptions = createFilterOptions<CountryOption>({
  stringify: o => `${o.name} ${o.code}`,
});

export const CountryMultiSelect: React.FC<CountryMultiSelectProps> = ({
  value,
  onChange,
  label = 'Countries',
  placeholder,
  size = 'medium',
  fullWidth = true,
  disabled,
  required,
  error,
  helperText,
  sx,
}) => {
  const { l10n } = useLocalization();
  const options = React.useMemo(toOptions, []);
  const selectedOptions = React.useMemo(() => {
    if (!value?.length) return [];

    const normalized = value.map(v => v.trim().toLowerCase());

    return options.filter(o => {
      const nName = o.name.toLowerCase();
      const nCode = o.code.toLowerCase();
      return normalized.includes(nName) || normalized.includes(nCode);
    });
  }, [value, options]);

  const translatedPlaceholder = placeholder ?? l10n.getString("companies-markets-countries-placeholder", null, "_Start typing a country…")

  return (
    <Autocomplete<CountryOption, true, false, false>
      multiple
      options={options}
      value={selectedOptions}
      filterOptions={filterOptions}
      onChange={(_, opts) => {
        const selected = opts ?? [];
        const names = selected.map(o => o.name);
        onChange(names.length ? names : null, selected.length ? selected : null);
      }}
      getOptionLabel={opt => opt.name}
      isOptionEqualToValue={(option, value) => option.code === value.code}
      autoHighlight
      renderOption={(props, option) => (
        <Box
          key={option.code}
          component="li"
          sx={{ '& > img': { mr: 1.5, flexShrink: 0 } }}
          {...props}
        >
          {option.code && (
            <img
              loading="lazy"
              width="20"
              srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
              src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
              alt=""
            />
          )}
          {option.name} {option.code ? `(${option.code})` : ''}
        </Box>
      )}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          placeholder={!value?.length ? translatedPlaceholder : ''}
          size={size}
          fullWidth={fullWidth}
          disabled={disabled}
          required={required}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <InputAdornment position="start">
                  <PublicIcon fontSize="small" sx={{ color: 'info.main' }} />
                </InputAdornment>
                {params.InputProps.startAdornment}
              </>
            ),
          }}
        />
      )}
      componentsProps={{
        paper: {
          sx: (t) => ({
            border: '1px solid',
            borderColor: t.palette.primary.dark,
            borderRadius: 1,
          }),
        }
      }}
      sx={sx}
    />
  );
};

export default CountryMultiSelect;
